/**
 * SaveQueueContext for UI-Model-1 (API-Direct Approach)
 *
 * Manages auto-save functionality with debouncing, priority ordering,
 * and retry logic.
 *
 * Features:
 * - Debounced auto-save (2 seconds by default)
 * - Priority-based ordering (header > footer > navigation > canvas)
 * - Merge pending operations of same type
 * - Retry failed saves (max 3 attempts)
 *
 * Line limit: ~200 lines
 */

import React, { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { DirtySection, SaveOperation } from '../types/state.types';
import { SaveQueueContextValue } from '../types/context.types';
import { generateId } from '../utils/idGenerator';

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_DEBOUNCE_MS = 2000;
const MAX_RETRY_COUNT = 3;

const SECTION_PRIORITY: Record<DirtySection, number> = {
  header: 1,
  footer: 2,
  navigation: 3,
  theme: 4,
  customCss: 5,
  sharedWidgets: 6,
  canvas: 7,
};

// ============================================================================
// Context
// ============================================================================

const SaveQueueContext = createContext<SaveQueueContextValue | null>(null);

interface SaveQueueProviderProps {
  children: React.ReactNode;
  debounceMs?: number;
  onSave?: (section: DirtySection, data: unknown, canvasId?: string) => Promise<void>;
  onSaveError?: (error: Error, section: DirtySection) => void;
}

export const SaveQueueProvider = ({
  children,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  onSave,
  onSaveError,
}: SaveQueueProviderProps) => {
  const [queue, setQueue] = useState<SaveOperation[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState<number | null>(null);
  const [processingVersion, setProcessingVersion] = useState(0);

  const debounceTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const pendingDebounceData = useRef<Map<string, { section: DirtySection; data: unknown; canvasId?: string }>>(new Map());
  const retryTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const isProcessing = useRef(false);

  // Derived state
  const hasPendingSaves = queue.some(op => op.status === 'pending');
  const pendingCount = queue.filter(op => op.status === 'pending').length;
  const hasFailedSaves = queue.some(op => op.status === 'failed');

  // ============================================================================
  // Internal Helpers
  // ============================================================================

  /** Get a debounce timer key for a section/canvas pair */
  const getTimerKey = (section: DirtySection, canvasId?: string): string =>
    canvasId ? `${section}_${canvasId}` : section;

  /** Clear and remove a debounce timer by key */
  const clearTimer = (key: string) => {
    const timer = debounceTimers.current.get(key);
    if (timer) {
      clearTimeout(timer);
      debounceTimers.current.delete(key);
    }
  };

  /** Clear all debounce timers */
  const clearAllTimers = () => {
    debounceTimers.current.forEach(timer => clearTimeout(timer));
    debounceTimers.current.clear();
  };

  /** Update a single operation's status/fields in the queue */
  const updateOperationStatus = (opId: string, updates: Partial<SaveOperation>) => {
    setQueue(prev =>
      prev.map(item =>
        item.id === opId ? { ...item, ...updates, updatedAt: Date.now() } : item
      )
    );
  };

  /** Handle a save failure — retry with exponential backoff, or mark as failed */
  const handleSaveFailure = (op: SaveOperation, error: unknown) => {
    const newRetryCount = op.retryCount + 1;
    if (newRetryCount >= MAX_RETRY_COUNT) {
      updateOperationStatus(op.id, { status: 'failed' as const, retryCount: newRetryCount });
      onSaveError?.(error as Error, op.type);
    } else {
      // Keep as in_progress during backoff; after delay, set to pending to trigger retry
      const delay = 1000 * Math.pow(2, newRetryCount - 1); // 1s, 2s, 4s
      updateOperationStatus(op.id, { retryCount: newRetryCount });
      const timer = setTimeout(() => {
        retryTimers.current.delete(op.id);
        updateOperationStatus(op.id, { status: 'pending' as const });
      }, delay);
      retryTimers.current.set(op.id, timer);
    }
  };

  /** Merge data into an existing pending operation, or create a new one */
  const mergeOrAddOperation = (
    prev: SaveOperation[],
    section: DirtySection,
    data: unknown,
    canvasId?: string
  ): SaveOperation[] => {
    const existingIndex = prev.findIndex(
      op => op.type === section && op.canvasId === canvasId && op.status === 'pending'
    );

    if (existingIndex >= 0) {
      const updated = [...prev];
      updated[existingIndex] = { ...updated[existingIndex], data, updatedAt: Date.now() };
      return updated;
    }

    const newOp: SaveOperation = {
      id: generateId('save'),
      type: section,
      canvasId,
      data,
      priority: SECTION_PRIORITY[section],
      retryCount: 0,
      maxRetries: MAX_RETRY_COUNT,
      status: 'pending',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    return [...prev, newOp];
  };

  // ============================================================================
  // Effects
  // ============================================================================

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
      retryTimers.current.forEach(timer => clearTimeout(timer));
      retryTimers.current.clear();
    };
  }, []);

  // Process queue when items are added
  useEffect(() => {
    if (!isProcessing.current && hasPendingSaves) {
      processQueue();
    }
  }, [queue, hasPendingSaves, processingVersion]);

  // ============================================================================
  // Queue Operations
  // ============================================================================

  const processQueue = useCallback(async () => {
    if (isProcessing.current || !onSave) return;

    isProcessing.current = true;
    setIsSaving(true);

    try {
      // Sort by priority
      const pendingOps = queue
        .filter(op => op.status === 'pending')
        .sort((a, b) => a.priority - b.priority);

      for (const op of pendingOps) {
        updateOperationStatus(op.id, { status: 'in_progress' as const });

        try {
          await onSave(op.type, op.data, op.canvasId);
          // Remove completed op AND any stale failed ops for the same section
          setQueue(prev => prev.filter(item => {
            if (item.id === op.id) return false;
            if (item.type === op.type && item.canvasId === op.canvasId && item.status === 'failed') return false;
            return true;
          }));
          setLastSaveTime(Date.now());
        } catch (error) {
          handleSaveFailure(op, error);
        }
      }
    } finally {
      isProcessing.current = false;
      setIsSaving(false);
      setProcessingVersion(v => v + 1);
    }
  }, [queue, onSave, onSaveError]);

  const enqueueSave = useCallback(
    (section: DirtySection, data: unknown, canvasId?: string) => {
      const timerKey = getTimerKey(section, canvasId);
      clearTimer(timerKey);

      pendingDebounceData.current.set(timerKey, { section, data, canvasId });

      const timer = setTimeout(() => {
        debounceTimers.current.delete(timerKey);
        pendingDebounceData.current.delete(timerKey);
        setQueue(prev => mergeOrAddOperation(prev, section, data, canvasId));
      }, debounceMs);

      debounceTimers.current.set(timerKey, timer);
    },
    [debounceMs]
  );

  const saveNow = useCallback(
    async (section: DirtySection, data: unknown, canvasId?: string) => {
      if (!onSave) return;

      clearTimer(getTimerKey(section, canvasId));

      // Remove from queue if pending
      setQueue(prev => prev.filter(op => !(op.type === section && op.canvasId === canvasId)));

      // Execute immediately
      setIsSaving(true);
      try {
        await onSave(section, data, canvasId);
        setLastSaveTime(Date.now());
      } catch (error) {
        onSaveError?.(error as Error, section);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [onSave, onSaveError]
  );

  const saveAll = useCallback(async () => {
    // Flush all debounced items into the queue immediately instead of discarding
    debounceTimers.current.forEach((timer, key) => {
      clearTimeout(timer);
      debounceTimers.current.delete(key);
    });
    const pendingItems = Array.from(pendingDebounceData.current.values());
    pendingDebounceData.current.clear();

    if (pendingItems.length > 0) {
      setQueue(prev => {
        let updated = prev;
        for (const { section, data, canvasId } of pendingItems) {
          updated = mergeOrAddOperation(updated, section, data, canvasId);
        }
        return updated;
      });
    }

    await processQueue();
  }, [processQueue]);

  const cancelPending = useCallback((section?: DirtySection) => {
    if (!section) {
      clearAllTimers();
      setQueue(prev => prev.filter(op => op.status !== 'pending'));
      return;
    }
    clearTimer(section);
    setQueue(prev => prev.filter(op => op.type !== section));
  }, []);

  const retryFailed = useCallback(() => {
    setQueue(prev =>
      prev.map(op =>
        op.status === 'failed'
          ? { ...op, status: 'pending' as const, retryCount: 0, updatedAt: Date.now() }
          : op
      )
    );
  }, []);

  const value = useMemo<SaveQueueContextValue>(
    () => ({
      hasPendingSaves,
      isSaving,
      pendingCount,
      lastSaveTime,
      hasFailedSaves,
      enqueueSave,
      saveNow,
      saveAll,
      cancelPending,
      retryFailed,
    }),
    [hasPendingSaves, isSaving, pendingCount, lastSaveTime, hasFailedSaves, enqueueSave, saveNow, saveAll, cancelPending, retryFailed]
  );

  return (
    <SaveQueueContext.Provider value={value}>
      {children}
    </SaveQueueContext.Provider>
  );
};

export const useSaveQueue = (): SaveQueueContextValue => {
  const context = useContext(SaveQueueContext);
  if (!context) {
    throw new Error('useSaveQueue must be used within a SaveQueueProvider');
  }
  return context;
};

export default SaveQueueContext;
